import { stringify } from 'csv-stringify';
import AdmZip from 'adm-zip';
import { uzipGtfsData } from '../libs/gtfs-lib';
import {parse} from 'csv-parse/dist/esm/sync';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import isBetween from 'dayjs/plugin/isBetween';
import { keyBy } from 'lodash';

// Extend dayjs with plugins
dayjs.extend(minMax);
dayjs.extend(isBetween);

const addContractsToObject = (target, contractIds) => {
    if (!target || !Array.isArray(contractIds) || contractIds.length === 0) {
        return;
    }
    target.contractIds = target.contractIds || [];
    contractIds.forEach((contractId) => {
        if (contractId && !target.contractIds.includes(contractId)) {
            target.contractIds.push(contractId);
        }
    });
};

export class GTFSProcessor {
    constructor() {
        this.data = {
            agency: [],
            routes: [],
            trips: [],
            stopTimes: [],
            stops: [],
            calendar: [],
            calendarDates: [],
            shapes: [],
            routeVariants: [],
            deadruns: [],
            shifts: []
        };
        this.loaded = false;
    }

    async loadGTFSFile(file) {
        try {
            console.log('Starting to load GTFS file:', file.name);
            const arrayBuffer = await file.arrayBuffer();
            const buffer = Buffer.from(arrayBuffer);
            const gtfsData = uzipGtfsData(buffer, parse);

            console.log('gtfsData', gtfsData);
            
            // Map network_id to contractId for each route
            const routes = gtfsData.routes.map(route => ({
                ...route,
                contractId: route.network_id || route.agency_id || ''
            }));

            this.data = {
                agency: gtfsData.agency || [],
                routes: routes || [],
                trips: gtfsData.trips || [],
                stopTimes: gtfsData.stopTimes || [],
                stops: gtfsData.stops || [],
                calendar: gtfsData.calendars || [],
                calendarDates: gtfsData.calendarDates || [],
                shapes: gtfsData.shapes || [],
                routeVariants: gtfsData.routeVariants || [],
                deadruns: gtfsData.deadruns || [],
                shifts: gtfsData.shifts || []
            };
            this._serviceContractLookup = null;
            this._shiftContractMap = null;

            this.loaded = true;
            return this.getDateRange();
        } catch (error) {
            console.error('Error loading GTFS file:', error);
            throw error;
        }
    }

    getDateRange() {
        if (!this.data.calendar || this.data.calendar.length === 0) {
            return null;
        }

        const dates = this.data.calendar.map(entry => ({
            start: dayjs(entry.start_date, 'YYYYMMDD'),
            end: dayjs(entry.end_date, 'YYYYMMDD')
        }));

        const startDate = dayjs.min(dates.map(d => d.start));
        const endDate = dayjs.max(dates.map(d => d.end));

        return {
            start: startDate.format('YYYY-MM-DD'),
            end: endDate.format('YYYY-MM-DD')
        };
    }

    // Helper to calculate distance between two stops
    calculateDistance(stop1, stop2) {
        const R = 6371; // Earth's radius in km
        const lat1 = parseFloat(stop1.stop_lat);
        const lon1 = parseFloat(stop1.stop_lon);
        const lat2 = parseFloat(stop2.stop_lat);
        const lon2 = parseFloat(stop2.stop_lon);

        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
                Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return R * c;
    }

    // Helper to check if a service runs on a given date
    doesServiceRunOnDate(service, date) {
        // First check if date is within service's date range
        const serviceStart = dayjs(service.start_date, 'YYYYMMDD').startOf('day');
        const serviceEnd = dayjs(service.end_date, 'YYYYMMDD').endOf('day');
        const checkDate = date.startOf('day');
        
        if (!checkDate.isBetween(serviceStart, serviceEnd, 'day', '[]')) {
            return false;
        }

        const dayOfWeek = checkDate.day();
        const dayMap = {
            0: 'sunday',
            1: 'monday',
            2: 'tuesday',
            3: 'wednesday',
            4: 'thursday',
            5: 'friday',
            6: 'saturday'
        };

        // Check calendar_dates for exceptions
        const dateStr = checkDate.format('YYYYMMDD');
        const exception = this.data.calendarDates.find(cd => 
            cd.service_id === service.service_id && 
            cd.date === dateStr
        );

        if (exception) {
            // 1 = service added, 2 = service removed
            return exception.exception_type === '1';
        }

        // Check regular calendar
        return service[dayMap[dayOfWeek]] === '1';
    }

    // Calculate distance using shape points if available, otherwise use stops
    calculateTripDistance(trip, stopTimes) {
        // Get route variant distance from route_variants.txt
        const routeVariant = this.data.routeVariants.find(rv => rv.route_variant_id === trip.route_variant_id);
        if (routeVariant) {
            return parseFloat(routeVariant.route_variant_distance_km);
        }
        return 0;
    }

    // Calculate trip duration in hours (first stop departure to last stop arrival)
    calculateTripDuration(trip, stopTimes) {
        if (!stopTimes || stopTimes.length < 2) {
            return 0;
        }

        // Sort stop times by sequence
        const sortedStopTimes = stopTimes.sort((a, b) => parseInt(a.stop_sequence) - parseInt(b.stop_sequence));
        
        // Get first and last stop times
        const firstStop = sortedStopTimes[0];
        const lastStop = sortedStopTimes[sortedStopTimes.length - 1];
        
        // Get departure time from first stop and arrival time from last stop
        let departureTime = firstStop.departure_time || firstStop.arrival_time;
        let arrivalTime = lastStop.arrival_time || lastStop.departure_time;
        
        if (!departureTime || !arrivalTime) {
            return 0;
        }

        // Convert GTFS time format (HH:MM:SS) to hours
        const departureHours = this.convertGTFSTimeToHours(departureTime);
        const arrivalHours = this.convertGTFSTimeToHours(arrivalTime);
        
        // Handle trips that span midnight (arrival time is less than departure time)
        let duration = arrivalHours - departureHours;
        if (duration < 0) {
            duration += 24; // Add 24 hours for trips spanning midnight
        }
        
        return duration;
    }

    // Convert GTFS time format (HH:MM:SS) to decimal hours
    convertGTFSTimeToHours(gtfsTime) {
        if (!gtfsTime) return 0;
        
        const parts = gtfsTime.split(':').map(Number);
        if (parts.length < 2) return 0;
        
        const hours = parts[0];
        const minutes = parts[1] || 0;
        const seconds = parts[2] || 0;
        
        return hours + (minutes / 60) + (seconds / 3600);
    }

    // Calculate service kilometers for a route in a given month
    calculateRouteKilometers(routeId, month) {
        const trips = this.data.trips.filter(trip => trip.route_id === routeId);
        let totalKm = 0;

        trips.forEach(trip => {
            const service = this.data.calendar.find(s => s.service_id === trip.service_id);
            if (!service) return;

            // Get stop times for this trip
            const stopTimes = this.data.stopTimes
                .filter(st => st.trip_id === trip.trip_id)
                .sort((a, b) => parseInt(a.stop_sequence) - parseInt(b.stop_sequence));

            // Calculate trip distance using shape points
            const tripDistance = this.calculateTripDistance(trip, stopTimes);

            // Count how many times this service runs in the month
            const daysInMonth = month.daysInMonth();
            let serviceCount = 0;
            for (let day = 1; day <= daysInMonth; day++) {
                const date = month.date(day);
                if (this.doesServiceRunOnDate(service, date)) {
                    serviceCount++;
                }
            }

            totalKm += tripDistance * serviceCount;
        });

        return totalKm;
    }

    // Calculate service hours for a route in a given month
    calculateRouteHours(routeId, month) {
        const trips = this.data.trips.filter(trip => trip.route_id === routeId);
        let totalHours = 0;

        trips.forEach(trip => {
            const service = this.data.calendar.find(s => s.service_id === trip.service_id);
            if (!service) return;

            // Get stop times for this trip
            const stopTimes = this.data.stopTimes
                .filter(st => st.trip_id === trip.trip_id)
                .sort((a, b) => parseInt(a.stop_sequence) - parseInt(b.stop_sequence));

            // Calculate trip duration in hours
            const tripDuration = this.calculateTripDuration(trip, stopTimes);

            // Count how many times this service runs in the month
            const daysInMonth = month.daysInMonth();
            let serviceCount = 0;
            for (let day = 1; day <= daysInMonth; day++) {
                const date = month.date(day);
                if (this.doesServiceRunOnDate(service, date)) {
                    serviceCount++;
                }
            }

            totalHours += tripDuration * serviceCount;
        });

        return totalHours;
    }

    // Calculate monthly service statistics
    calculateMonthlyServiceStats(startDate, endDate) {
        const start = dayjs(startDate).startOf('day');
        const end = dayjs(endDate).endOf('day');
        const months = [];
        let current = start.clone().startOf('month');

        // Pre-process trips by route
        const tripsByRoute = {};
        this.data.trips.forEach(trip => {
            if (!tripsByRoute[trip.route_id]) {
                tripsByRoute[trip.route_id] = [];
            }
            tripsByRoute[trip.route_id].push(trip);
        });

        // Pre-process stop times by trip
        const stopTimesByTrip = {};
        this.data.stopTimes.forEach(st => {
            if (!stopTimesByTrip[st.trip_id]) {
                stopTimesByTrip[st.trip_id] = [];
            }
            stopTimesByTrip[st.trip_id].push(st);
        });

        // Pre-process stops lookup
        const stopsById = {};
        this.data.stops.forEach(stop => {
            stopsById[stop.stop_id] = stop;
        });

        // Pre-process agency lookup
        const agencyById = {};
        this.data.agency.forEach(agency => {
            agencyById[agency.agency_id] = agency;
        });

        const { tripContracts } = this.getServiceContractLookup();

        const routeVariantsById = keyBy(this.data.routeVariants || [], 'route_variant_id');

        while (current.isBefore(end) || current.isSame(end, 'month')) {
            const monthData = {
                month: current.format('YYYY-MM'),
                routeKilometers: {},
                routeHours: {},
                totalTrips: 0,
                servicesByRoute: {},
                tripsByDay: {},
                agencyStats: {}
            };

            // Process each route
            this.data.routes.forEach(route => {
                const routeTrips = tripsByRoute[route.route_id] || [];
                let routeKm = 0;
                let routeHours = 0;
                let routeServices = new Set();
                let tripCount = 0;

                routeTrips.forEach(trip => {
                    const service = this.data.calendar.find(s => s.service_id === trip.service_id);
                    if (!service) return;

                    // Get stop times for this trip
                    const stopTimes = (stopTimesByTrip[trip.trip_id] || [])
                        .sort((a, b) => parseInt(a.stop_sequence) - parseInt(b.stop_sequence));

                    // Calculate trip distance using shapes if available
                    const tripDistance = this.calculateTripDistance(trip, stopTimes);
                    // Calculate trip duration in hours
                    const tripDuration = this.calculateTripDuration(trip, stopTimes);

                    // Count service days in this month (using same clamping logic as other reports)
                const serviceDaysInMonth = this.getClampedServiceDaysInMonth(service, current, startDate, endDate);

                if (serviceDaysInMonth === 0) {
                    return;
                }

                // Track trips by day for the clamped date range
                const monthKey = current.format('YYYY-MM');
                const monthStart = current.startOf('month');
                const monthEnd = current.endOf('month');
                const clampedStart = start.isAfter(monthStart) ? start : monthStart;
                const clampedEnd = end.isBefore(monthEnd) ? end : monthEnd;
                let dateCursor = clampedStart.clone();
                while (dateCursor.isSameOrBefore(clampedEnd, 'day')) {
                    if (this.doesServiceRunOnDate(service, dateCursor)) {
                        const dayKey = dateCursor.format('YYYY-MM-DD');
                        if (!monthData.tripsByDay[dayKey]) {
                            monthData.tripsByDay[dayKey] = 0;
                        }
                        monthData.tripsByDay[dayKey]++;
                    }
                    dateCursor = dateCursor.add(1, 'day');
                }

                    routeKm += tripDistance * serviceDaysInMonth;
                    routeHours += tripDuration * serviceDaysInMonth;
                    monthData.totalTrips += serviceDaysInMonth;
                    tripCount += serviceDaysInMonth;
                    if (serviceDaysInMonth > 0) {
                        routeServices.add(service.service_id);
                    }

                    const tripContractSet = tripContracts[trip.trip_id];
                    const tripContractIds = tripContractSet ? Array.from(tripContractSet) : [];
                    if (!tripContractIds.length && route.contractId) {
                        tripContractIds.push(route.contractId);
                    }

                    if (!monthData.routeKilometers[route.route_id]) {
                        monthData.routeKilometers[route.route_id] = {
                            routeNumber: route.route_short_name || route.route_id,
                            kilometers: 0,
                            serviceCount: 0,
                            tripCount: 0,
                            uniqueServiceCount: 0,
                            agencyId: route.agency_id,
                            agencyName: route.agency_id ? agencyById[route.agency_id]?.agency_name : null,
                            variants: {},
                            contractIds: []
                        };
                    }

                    if (!monthData.routeHours[route.route_id]) {
                        monthData.routeHours[route.route_id] = {
                            routeNumber: route.route_short_name || route.route_id,
                            hours: 0,
                            serviceCount: 0,
                            tripCount: 0,
                            uniqueServiceCount: 0,
                            agencyId: route.agency_id,
                            agencyName: route.agency_id ? agencyById[route.agency_id]?.agency_name : null,
                            variants: {},
                            contractIds: []
                        };
                    }

                    addContractsToObject(monthData.routeKilometers[route.route_id], tripContractIds);
                    addContractsToObject(monthData.routeHours[route.route_id], tripContractIds);

                    const variantId = trip.route_variant_id || trip.shape_id || trip.trip_id;
                    if (variantId) {
                        const variantInfo = routeVariantsById[variantId];
                        const variantName = variantInfo?.route_variant_name || variantInfo?.route_variant_desc || variantId;
                        const routeVariantNumber = variantInfo?.route_variant_number;

                        const routeKilometerEntry = monthData.routeKilometers[route.route_id];
                        const kilometerVariants = routeKilometerEntry.variants;
                        if (!kilometerVariants[variantId]) {
                            kilometerVariants[variantId] = {
                                variantId,
                                variantName,
                                variantNumber: routeVariantNumber,
                                baseKilometers: tripDistance,
                                tripCount: 0,
                                totalKilometers: 0,
                                contractIds: []
                            };
                        }
                        addContractsToObject(kilometerVariants[variantId], tripContractIds);
                        kilometerVariants[variantId].tripCount += serviceDaysInMonth;
                        kilometerVariants[variantId].totalKilometers += tripDistance * serviceDaysInMonth;
                        if (!Number.isFinite(kilometerVariants[variantId].baseKilometers)) {
                            kilometerVariants[variantId].baseKilometers = tripDistance;
                        }

                        const routeHourEntry = monthData.routeHours[route.route_id];
                        const hourVariants = routeHourEntry.variants;
                        if (!hourVariants[variantId]) {
                            hourVariants[variantId] = {
                                variantId,
                                variantName,
                                variantNumber: routeVariantNumber,
                                baseHours: tripDuration,
                                tripCount: 0,
                                totalHours: 0,
                                contractIds: []
                            };
                        }
                        addContractsToObject(hourVariants[variantId], tripContractIds);
                        hourVariants[variantId].tripCount += serviceDaysInMonth;
                        hourVariants[variantId].totalHours += tripDuration * serviceDaysInMonth;
                        if (!Number.isFinite(hourVariants[variantId].baseHours)) {
                            hourVariants[variantId].baseHours = tripDuration;
                        }
                    }

                    // Track agency stats
                    if (route.agency_id && agencyById[route.agency_id]) {
                        const agencyId = route.agency_id;
                        if (!monthData.agencyStats[agencyId]) {
                            monthData.agencyStats[agencyId] = {
                                name: agencyById[agencyId].agency_name,
                                kilometers: 0,
                                hours: 0,
                                trips: 0,
                                routes: new Set()
                            };
                        }
                        monthData.agencyStats[agencyId].kilometers += tripDistance * serviceDaysInMonth;
                        monthData.agencyStats[agencyId].hours += tripDuration * serviceDaysInMonth;
                        monthData.agencyStats[agencyId].trips += serviceDaysInMonth;
                        if (serviceDaysInMonth > 0) {
                            monthData.agencyStats[agencyId].routes.add(route.route_id);
                        }
                    }
                });

                if (routeKm > 0) {
                    const uniqueServiceCount = routeServices.size;
                    const routeEntry = monthData.routeKilometers[route.route_id] || {
                        routeNumber: route.route_short_name || route.route_id,
                        kilometers: 0,
                        serviceCount: 0,
                        uniqueServiceCount: 0,
                        tripCount: 0,
                        agencyId: route.agency_id,
                        agencyName: route.agency_id ? agencyById[route.agency_id]?.agency_name : null,
                        variants: {},
                        contractIds: []
                    };
                    routeEntry.kilometers += routeKm;
                    routeEntry.serviceCount += tripCount;
                    routeEntry.tripCount += tripCount;
                    routeEntry.uniqueServiceCount = (routeEntry.uniqueServiceCount || 0) + uniqueServiceCount;
                    monthData.routeKilometers[route.route_id] = routeEntry;
                }

                if (routeHours > 0) {
                    const uniqueServiceCount = routeServices.size;
                    const routeEntry = monthData.routeHours[route.route_id] || {
                        routeNumber: route.route_short_name || route.route_id,
                        hours: 0,
                        serviceCount: 0,
                        uniqueServiceCount: 0,
                        tripCount: 0,
                        agencyId: route.agency_id,
                        agencyName: route.agency_id ? agencyById[route.agency_id]?.agency_name : null,
                        variants: {},
                        contractIds: []
                    };
                    routeEntry.hours += routeHours;
                    routeEntry.serviceCount += tripCount;
                    routeEntry.tripCount += tripCount;
                    routeEntry.uniqueServiceCount = (routeEntry.uniqueServiceCount || 0) + uniqueServiceCount;
                    monthData.routeHours[route.route_id] = routeEntry;
                }
            });

            // Convert agency stats Sets to arrays for easier handling
            Object.values(monthData.agencyStats).forEach(stats => {
                stats.routes = stats.routes.size;
            });

            months.push(monthData);
            current = current.add(1, 'month');
        }

        return months;
    }

    generateOSR(params) {
        console.log('Generating OSR with params:', params);
        
        if (!this.loaded) {
            throw new Error('GTFS data not loaded. Please upload a valid GTFS file.');
        }

        try {
            const { startDate, endDate } = params;
            
            // Validate date range
            if (!startDate || !endDate) {
                throw new Error('Start and end dates are required for the report.');
            }
            
            // Check if we have valid service data for the date range
            const dateRange = this.getDateRange();
            if (!dateRange.start || !dateRange.end) {
                throw new Error('The GTFS data does not contain valid service dates.');
            }
            
            // Convert dates to dayjs objects for comparison
            const start = dayjs(startDate);
            const end = dayjs(endDate);
            const gtfsStart = dayjs(dateRange.start);
            const gtfsEnd = dayjs(dateRange.end);
            
            // Warn if date range is partially or completely outside GTFS coverage
            if (start.isBefore(gtfsStart) || end.isAfter(gtfsEnd)) {
                console.warn('Date range is partially outside GTFS data coverage:', {
                    requestedRange: { start: startDate, end: endDate },
                    gtfsDataRange: dateRange
                });
            }
            
            console.log('Calculating monthly statistics...');
            const monthlyStats = this.calculateMonthlyServiceStats(startDate, endDate);
            
            // If no monthly stats are returned, show a friendly error
            if (!monthlyStats || monthlyStats.length === 0) {
                throw new Error('No service data found for the selected date range. Try adjusting your dates.');
            }
            
            console.log('Generating report sections...');
            const routeSummary = this.generateRouteSummary(monthlyStats);
            const routeHoursSummary = this.generateRouteHoursSummary(monthlyStats);
            const tripSummary = this.generateTripSummary(monthlyStats, params);
            const agencySummary = this.generateAgencySummary(monthlyStats);
            const insights = this.generateInsights(monthlyStats);
            const deadrunSummary = this.generateDeadrunSummary(monthlyStats, startDate, endDate);
            const dutyHoursSummary = this.generateDutyHoursSummary(monthlyStats, startDate, endDate);
            
            // Calculate additional operational metrics
            const operationalMetrics = this.calculateOperationalMetrics(monthlyStats);
            
            console.log('OSR generation complete');
            return {
                monthlyStats,
                routeSummary,
                routeHoursSummary,
                tripSummary,
                agencySummary,
                insights,
                operationalMetrics,
                deadrunSummary,
                dutyHoursSummary,
                // Include date range information in the report
                reportInfo: {
                    generatedAt: new Date().toISOString(),
                    dateRange: { startDate, endDate },
                    gtfsDateRange: dateRange,
                    dataWarnings: this.getDataWarnings(startDate, endDate, dateRange)
                }
            };
        } catch (error) {
            console.error('Error generating OSR report:', error);
            throw new Error(`Failed to generate report: ${error.message}`);
        }
    }

    generateRouteSummary(monthlyStats) {
        const routeSummary = {};
        
        monthlyStats.forEach(monthData => {
            Object.entries(monthData.routeKilometers).forEach(([routeId, routeData]) => {
                if (!routeSummary[routeId]) {
                    // Get the route from GTFS data to get network_id
                    const route = this.data.routes.find(r => r.route_id === routeId);
                    routeSummary[routeId] = {
                        routeId,
                        routeNumber: routeData.routeNumber,
                        networkId: route?.network_id || route?.contractId || '',
                        monthlyKilometers: [],
                        totalServices: 0,
                        variants: {},
                        contractIds: []
                    };
                }
                routeSummary[routeId].monthlyKilometers.push({
                    month: monthData.month,
                    kilometers: routeData.kilometers,
                    serviceCount: routeData.serviceCount,
                    tripCount: routeData.tripCount,
                    contractIds: routeData.contractIds || []
                });
                routeSummary[routeId].totalServices += routeData.serviceCount;
                addContractsToObject(routeSummary[routeId], routeData.contractIds || []);

                Object.values(routeData.variants || {}).forEach(variant => {
                    if (!routeSummary[routeId].variants[variant.variantId]) {
                        routeSummary[routeId].variants[variant.variantId] = {
                            variantId: variant.variantId,
                            variantName: variant.variantName,
                            variantNumber: variant.variantNumber,
                            baseKilometers: variant.baseKilometers,
                            tripCount: 0,
                            totalKilometers: 0,
                            contractIds: []
                        };
                    }
                    const variantEntry = routeSummary[routeId].variants[variant.variantId];
                    variantEntry.tripCount += variant.tripCount;
                    variantEntry.totalKilometers += variant.totalKilometers;
                    if (Number.isFinite(variant.baseKilometers)) {
                        variantEntry.baseKilometers = variant.baseKilometers;
                    }
                    addContractsToObject(variantEntry, variant.contractIds || []);
                });
            });
        });

        return Object.values(routeSummary).map(route => ({
            ...route,
            variants: Object.values(route.variants || {})
        }));
    }

    generateRouteHoursSummary(monthlyStats) {
        const routeSummary = {};
        
        monthlyStats.forEach(monthData => {
            Object.entries(monthData.routeHours || {}).forEach(([routeId, routeData]) => {
                if (!routeSummary[routeId]) {
                    // Get the route from GTFS data to get network_id
                    const route = this.data.routes.find(r => r.route_id === routeId);
                    routeSummary[routeId] = {
                        routeId,
                        routeNumber: routeData.routeNumber,
                        networkId: route?.network_id || route?.contractId || '',
                        monthlyHours: [],
                        totalServices: 0,
                        variants: {},
                        contractIds: []
                    };
                }
                routeSummary[routeId].monthlyHours.push({
                    month: monthData.month,
                    hours: routeData.hours,
                    serviceCount: routeData.serviceCount,
                    tripCount: routeData.tripCount,
                    contractIds: routeData.contractIds || []
                });
                routeSummary[routeId].totalServices += routeData.serviceCount;
                addContractsToObject(routeSummary[routeId], routeData.contractIds || []);

                Object.values(routeData.variants || {}).forEach(variant => {
                    if (!routeSummary[routeId].variants[variant.variantId]) {
                        routeSummary[routeId].variants[variant.variantId] = {
                            variantId: variant.variantId,
                            variantName: variant.variantName,
                            variantNumber: variant.variantNumber,
                            baseHours: variant.baseHours,
                            tripCount: 0,
                            totalHours: 0,
                            contractIds: []
                        };
                    }
                    const variantEntry = routeSummary[routeId].variants[variant.variantId];
                    variantEntry.tripCount += variant.tripCount;
                    variantEntry.totalHours += variant.totalHours;
                    if (Number.isFinite(variant.baseHours)) {
                        variantEntry.baseHours = variant.baseHours;
                    }
                    addContractsToObject(variantEntry, variant.contractIds || []);
                });
            });
        });

        return Object.values(routeSummary).map(route => ({
            ...route,
            variants: Object.values(route.variants || {})
        }));
    }

    generateTripSummary(monthlyStats, params) {
        if (!monthlyStats || monthlyStats.length === 0) {
            return null;
        }

        // Original monthly overview data
        const summary = {
            totalTrips: 0,
            totalKilometers: 0,
            averageTripsPerDay: 0,
            tripsPerMonth: {}
        };

        // Get the service days to calculate averages
        let totalDays = 0;
        monthlyStats.forEach(month => {
            const [year, monthNum] = month.month.split('-');
            const daysInMonth = new Date(year, parseInt(monthNum), 0).getDate();
            totalDays += daysInMonth;
            
            // Add month data to summary
            summary.totalTrips += month.totalTrips || 0;
            summary.totalKilometers += month.totalKilometers || 0;
            summary.tripsPerMonth[month.month] = month.totalTrips || 0;
        });

        // Calculate average trips per day
        summary.averageTripsPerDay = totalDays > 0 ? Math.round(summary.totalTrips / totalDays) : 0;

        // Create a structure for route-based trip counts
        const routeTripCounts = [];
        const uniqueRoutes = new Set();
        
        // First, collect all unique routes across all months
        monthlyStats.forEach(month => {
            if (month.routeKilometers) {
                Object.keys(month.routeKilometers).forEach(routeId => {
                    uniqueRoutes.add(routeId);
                });
            }
        });
        
        // Then, initialize the route trip counts structure
        uniqueRoutes.forEach(routeId => {
            // Get route details from the first month that has this route
            let routeNumber = null;
            for (const month of monthlyStats) {
                if (month.routeKilometers && month.routeKilometers[routeId]) {
                    routeNumber = month.routeKilometers[routeId].routeNumber;
                    break;
                }
            }
            
            // Get the route from GTFS data to get network_id
            const route = this.data.routes.find(r => r.route_id === routeId);
            
            const routeData = {
                routeId,
                routeNumber: routeNumber || routeId,
                networkId: route?.network_id || route?.contractId || '',
                monthlyTripCounts: {}
            };
            
            // Initialize trip counts for each month
            monthlyStats.forEach(month => {
                routeData.monthlyTripCounts[month.month] = 0;
            });
            
            routeTripCounts.push(routeData);
        });
        
        // Populate trip counts from monthly stats
        monthlyStats.forEach(month => {
            if (month.routeKilometers) {
                Object.entries(month.routeKilometers).forEach(([routeId, routeData]) => {
                    // Find the corresponding route in our routeTripCounts array
                    const routeEntry = routeTripCounts.find(r => r.routeId === routeId);
                    if (routeEntry) {
                        // For each month, store the trip count for this route
                        routeEntry.monthlyTripCounts[month.month] = routeData.tripCount || 0;
                    }
                });
            }
        });
        
        // Add the route trip counts to the summary
        summary.routeTripCounts = routeTripCounts;

        return summary;
    }

    generateAgencySummary(monthlyStats) {
        const agencySummary = {};
        
        monthlyStats.forEach(month => {
            Object.entries(month.agencyStats).forEach(([agencyId, stats]) => {
                if (!agencySummary[agencyId]) {
                    agencySummary[agencyId] = {
                        name: stats.name,
                        monthlyStats: []
                    };
                }
                agencySummary[agencyId].monthlyStats.push({
                    month: month.month,
                    kilometers: stats.kilometers,
                    hours: stats.hours,
                    trips: stats.trips,
                    routes: stats.routes
                });
            });
        });

        return Object.values(agencySummary);
    }

    generateInsights(monthlyStats) {
        const insights = [];

        // Service Utilization
        const serviceUtilization = monthlyStats.map(month => {
            const [year, monthNum] = month.month.split('-');
            const daysInMonth = new Date(year, monthNum, 0).getDate();
            const activeDays = Object.values(month.routeKilometers).reduce((days, route) => 
                Math.max(days, route.serviceCount || 0), 0);
            
            return {
                month: month.month,
                utilization: ((activeDays / daysInMonth) * 100).toFixed(1)
            };
        });

        insights.push({
            title: 'Service Utilization',
            description: 'Percentage of days with active services per month',
            data: serviceUtilization.map(data => ({
                metric: data.month,
                value: `${data.utilization}%`
            }))
        });

        // Route Performance Analysis
        const routePerformance = {};
        monthlyStats.forEach(month => {
            Object.entries(month.routeKilometers).forEach(([routeId, stats]) => {
                if (!routePerformance[routeId]) {
                    routePerformance[routeId] = {
                        totalKm: 0,
                        totalServices: 0,
                        months: 0
                    };
                }
                routePerformance[routeId].totalKm += stats.kilometers || 0;
                routePerformance[routeId].totalServices += stats.serviceCount || 0;
                routePerformance[routeId].months += 1;
            });
        });

        const topRoutes = Object.entries(routePerformance)
            .map(([routeId, stats]) => ({
                routeId,
                avgKmPerMonth: stats.totalKm / stats.months,
                avgServicesPerMonth: stats.totalServices / stats.months
            }))
            .sort((a, b) => b.avgKmPerMonth - a.avgKmPerMonth)
            .slice(0, 5);

        insights.push({
            title: 'Top Performing Routes',
            description: 'Routes with highest average monthly service kilometers',
            data: topRoutes.map(route => ({
                metric: `Route ${route.routeId}`,
                value: `${Math.round(route.avgKmPerMonth).toLocaleString()} km/month`,
                subValue: `${Math.round(route.avgServicesPerMonth)} services/month`
            }))
        });

        // Monthly Trend Analysis
        const monthlyTrends = monthlyStats.map((month, index, array) => {
            const totalKm = Object.values(month.routeKilometers)
                .reduce((sum, route) => sum + (route.kilometers || 0), 0);
            const prevMonth = index > 0 ? array[index - 1] : null;
            const prevTotalKm = prevMonth ? Object.values(prevMonth.routeKilometers)
                .reduce((sum, route) => sum + (route.kilometers || 0), 0) : totalKm;
            const change = prevMonth ? ((totalKm - prevTotalKm) / prevTotalKm * 100).toFixed(1) : '0.0';

            return {
                month: month.month,
                totalKm,
                change
            };
        });

        insights.push({
            title: 'Monthly Service Growth',
            description: 'Month-over-month change in service kilometers',
            data: monthlyTrends.map(trend => ({
                metric: trend.month,
                value: `${trend.change}%`,
                subValue: `${Math.round(trend.totalKm).toLocaleString()} km total`
            }))
        });

        // Service Intensity Analysis
        const serviceIntensity = monthlyStats.map(month => {
            const [year, monthNum] = month.month.split('-');
            const daysInMonth = new Date(year, monthNum, 0).getDate();
            const totalKm = Object.values(month.routeKilometers)
                .reduce((sum, route) => sum + (route.kilometers || 0), 0);
            const avgKmPerDay = totalKm / daysInMonth;

            return {
                month: month.month,
                avgKmPerDay: Math.round(avgKmPerDay).toLocaleString()
            };
        });

        insights.push({
            title: 'Daily Service Intensity',
            description: 'Average service kilometers operated per day',
            data: serviceIntensity.map(data => ({
                metric: data.month,
                value: `${data.avgKmPerDay} km/day`
            }))
        });

        return insights;
    }

    // New method to check for data quality issues or warnings
    getDataWarnings(startDate, endDate, gtfsDateRange) {
        const warnings = [];
        
        // Check if date range falls outside GTFS data
        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const gtfsStart = dayjs(gtfsDateRange.start);
        const gtfsEnd = dayjs(gtfsDateRange.end);
        
        if (start.isBefore(gtfsStart)) {
            warnings.push({
                type: 'dateRangeWarning',
                message: `The start date (${startDate}) is before the GTFS data start date (${gtfsDateRange.start}).`
            });
        }
        
        if (end.isAfter(gtfsEnd)) {
            warnings.push({
                type: 'dateRangeWarning',
                message: `The end date (${endDate}) is after the GTFS data end date (${gtfsDateRange.end}).`
            });
        }
        
        // Check for incomplete data
        if (this.data.routes.length === 0) {
            warnings.push({
                type: 'missingDataWarning',
                message: 'The GTFS data does not contain any routes.'
            });
        }
        
        if (this.data.trips.length === 0) {
            warnings.push({
                type: 'missingDataWarning',
                message: 'The GTFS data does not contain any trips.'
            });
        }
        
        if (this.data.stopTimes.length === 0) {
            warnings.push({
                type: 'missingDataWarning',
                message: 'The GTFS data does not contain any stop times.'
            });
        }
        
        return warnings;
    }

    // New method to calculate additional operational metrics
    calculateOperationalMetrics(monthlyStats) {
        const metrics = {
            averageServiceKilometers: 0,
            averageStopsPerTrip: 0,
            serviceEfficiency: 0,
            routeCoverage: 0,
            serviceDistribution: {}
        };
        
        try {
            let totalKilometers = 0;
            let totalServices = 0;
            let totalTrips = 0;
            let totalStops = 0;
            
            // Process each month's data
            monthlyStats.forEach(month => {
                // Extract route data
                Object.values(month.routeKilometers).forEach(route => {
                    totalKilometers += route.kilometers || 0;
                    totalServices += route.serviceCount || 0;
                });
                
                // Extract trip data if available
                if (month.tripData) {
                    Object.values(month.tripData).forEach(trip => {
                        totalTrips += trip.count || 0;
                        totalStops += trip.stopCount || 0;
                    });
                }
            });
            
            // Calculate averages
            metrics.averageServiceKilometers = totalServices > 0 
                ? totalKilometers / totalServices 
                : 0;
                
            metrics.averageStopsPerTrip = totalTrips > 0 
                ? totalStops / totalTrips 
                : 0;
                
            // Calculate service efficiency (higher is better)
            metrics.serviceEfficiency = totalKilometers > 0 && totalServices > 0
                ? totalTrips / totalKilometers
                : 0;
                
            // Calculate route coverage
            const uniqueRoutes = new Set();
            monthlyStats.forEach(month => {
                Object.keys(month.routeKilometers).forEach(routeId => {
                    uniqueRoutes.add(routeId);
                });
            });
            
            metrics.routeCoverage = uniqueRoutes.size;
            
            // Calculate service distribution by day of week
            const dayDistribution = {
                'Monday': 0,
                'Tuesday': 0,
                'Wednesday': 0,
                'Thursday': 0,
                'Friday': 0,
                'Saturday': 0,
                'Sunday': 0
            };
            
            monthlyStats.forEach(month => {
                if (month.dayOfWeekData) {
                    Object.entries(month.dayOfWeekData).forEach(([day, value]) => {
                        dayDistribution[day] = (dayDistribution[day] || 0) + value;
                    });
                }
            });
            
            metrics.serviceDistribution = dayDistribution;
            
            return metrics;
        } catch (error) {
            console.error('Error calculating operational metrics:', error);
            return metrics;
        }
    }

    getServiceDaysInMonth(serviceId, month) {
        if (!serviceId || !this.data.calendar || !Array.isArray(this.data.calendar)) {
            return 0;
        }

        const calendar = this.data.calendar.find(c => c.service_id === serviceId);
        if (!calendar) {
            return 0;
        }

        const daysInMonth = month.daysInMonth();
        const serviceDaysMap = {};
        const dayMap = {
            0: 'sunday',
            1: 'monday',
            2: 'tuesday',
            3: 'wednesday',
            4: 'thursday',
            5: 'friday',
            6: 'saturday'
        };

        for (let day = 1; day <= daysInMonth; day++) {
            const date = month.date(day);
            const dayOfWeek = date.day();
            const isScheduled = calendar[dayMap[dayOfWeek]] === '1';
            serviceDaysMap[date.format('YYYYMMDD')] = isScheduled;
        }

        const exceptions = this.data.calendarDates?.filter(cd =>
            cd.service_id === serviceId &&
            month.isSame(dayjs(cd.date, 'YYYYMMDD'), 'month')
        ) || [];

        exceptions.forEach(exception => {
            if (exception.exception_type === '1') {
                serviceDaysMap[exception.date] = true;
            } else if (exception.exception_type === '2') {
                serviceDaysMap[exception.date] = false;
            }
        });

        return Object.values(serviceDaysMap).filter(Boolean).length;
    }

    // Helper function to get clamped service days within a reporting period
    // This is the same logic used in calculateMonthlyServiceStats for planner service KMS
    getClampedServiceDaysInMonth(service, month, startDate, endDate) {
        if (!service) {
            return 0;
        }

        const start = dayjs(startDate).startOf('day');
        const end = dayjs(endDate).endOf('day');
        const monthStart = month.startOf('month');
        const monthEnd = month.endOf('month');
        const clampedStart = start.isAfter(monthStart) ? start : monthStart;
        const clampedEnd = end.isBefore(monthEnd) ? end : monthEnd;

        if (clampedEnd.isBefore(clampedStart)) {
            return 0;
        }

        let serviceDaysInMonth = 0;
        let dateCursor = clampedStart.clone();
        while (dateCursor.isSameOrBefore(clampedEnd, 'day')) {
            if (this.doesServiceRunOnDate(service, dateCursor)) {
                serviceDaysInMonth++;
            }
            dateCursor = dateCursor.add(1, 'day');
        }

        return serviceDaysInMonth;
    }

    // Calculate dead-run kilometers for a driver shift in a given month
    // Clamps service days to the reporting period date range (same as planner service KMS)
    calculateDeadrunKilometers(shiftId, month, startDate, endDate) {
        // Find all dead-runs associated with this shift
        const deadruns = this.data.deadruns?.filter(dr => dr.shiftId === shiftId) || [];
        
        // Get the shift to determine its service days
        const shift = this.data.shifts?.find(s => s.shift_id === shiftId);
        if (!shift) {
            console.warn(`No shift found for shiftId: ${shiftId}`);
            return { kilometers: 0, serviceDays: 0 };
        }
        
        if (!shift.service_id) {
            console.warn(`No service_id found for shift: ${shiftId}`);
            return { kilometers: 0, serviceDays: 0 };
        }
        
        // Use the same clamping logic as planner service KMS
        const service = this.data.calendar.find(s => s.service_id === shift.service_id);
        const serviceDaysInMonth = this.getClampedServiceDaysInMonth(service, month, startDate, endDate);
        
        let totalKm = 0;
        
        // Now calculate the total kilometers for all deadruns in this shift
        deadruns.forEach(deadrun => {
            // Convert distance from meters to kilometers
            const distanceKm = parseFloat(deadrun.distance) / 1000;
            
            // Each deadrun occurs once per service day
            totalKm += distanceKm * serviceDaysInMonth;
        });
        
        return {
            kilometers: totalKm,
            serviceDays: serviceDaysInMonth
        };
    }

    // Generate dead-run summary
    generateDeadrunSummary(monthlyStats, startDate, endDate) {
        if (!this.data.deadruns || !Array.isArray(this.data.deadruns)) {
            return [];
        }

        // Get unique shift IDs
        const shiftIds = [...new Set(this.data.deadruns.map(dr => dr.shiftId))];
        const shiftsById = keyBy(this.data.shifts, 'shift_id');
        const shiftContractMap = this.getShiftContractMap();

        return shiftIds.map(shiftId => {
            const monthlyKilometers = monthlyStats.map(month => {
                const monthDate = dayjs(month.month, 'YYYY-MM');
                const { kilometers, serviceDays } = this.calculateDeadrunKilometers(shiftId, monthDate, startDate, endDate);
                return {
                    month: month.month,
                    kilometers,
                    serviceDays
                };
            });

            // Get dead-run details for this shift
            const shiftDeadruns = this.data.deadruns.filter(dr => dr.shiftId === shiftId);
            const deadrunTypes = [...new Set(shiftDeadruns.map(dr => dr.type))];
            const shift = shiftsById[shiftId];
            const contractIds = shiftContractMap[shiftId] || [];
            const deadrunDetails = shiftDeadruns.map(dr => ({
                type: dr.type,
                distance: (parseFloat(dr.distance) || 0) / 1000 // convert metres to kilometres
            }));

            return {
                shiftId,
                monthlyKilometers,
                deadrunTypes,
                totalDeadruns: shiftDeadruns.length,
                shiftName: shift?.driver_shift,
                vehicleShift: shift?.vehicle_shift,
                contractIds,
                deadrunDetails
            };
        });
    }

    generateDutyHoursSummary(monthlyStats, startDate, endDate) {
        if (!Array.isArray(this.data.shifts) || this.data.shifts.length === 0) {
            return [];
        }

        const shiftsGrouped = {};
        const shiftContractMap = this.getShiftContractMap();

        this.data.shifts.forEach(shift => {
            const shiftId = shift.shift_id;
            if (!shiftId) return;

            const durationSeconds = Number(shift.duration || 0);
            if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
                return;
            }

            if (!shiftsGrouped[shiftId]) {
                shiftsGrouped[shiftId] = {
                    shiftId,
                    shiftName: shift.driver_shift || '',
                    vehicleShift: shift.vehicle_shift || '',
                    services: {}
                };
            }

            if (shift.service_id && !shiftsGrouped[shiftId].services[shift.service_id]) {
                shiftsGrouped[shiftId].services[shift.service_id] = durationSeconds;
            }
        });

        return Object.values(shiftsGrouped).map(shift => {
            const monthlyHours = (monthlyStats || []).map(month => {
                const monthDate = dayjs(month.month, 'YYYY-MM');
                let hours = 0;
                let serviceDays = 0;

                Object.entries(shift.services).forEach(([serviceId, durationSeconds]) => {
                    // Use the same clamping logic as planner service KMS
                    const service = this.data.calendar.find(s => s.service_id === serviceId);
                    const daysInMonth = this.getClampedServiceDaysInMonth(service, monthDate, startDate, endDate);
                    
                    if (daysInMonth > 0) {
                        hours += (durationSeconds / 3600) * daysInMonth;
                        serviceDays += daysInMonth;
                    }
                });

                const roundedHours = Math.round(hours * 100) / 100;

                return {
                    month: month.month,
                    hours: roundedHours,
                    serviceDays
                };
            });

            const totalHours = monthlyHours.reduce((sum, entry) => sum + (entry.hours || 0), 0);
            const totalServiceDays = monthlyHours.reduce((sum, entry) => sum + (entry.serviceDays || 0), 0);
            const firstDurationSeconds = Object.values(shift.services)[0] || 0;
            const durationHours = Math.round((firstDurationSeconds / 3600) * 100) / 100;
            const contractIds = shiftContractMap[shift.shiftId] || [];

            return {
                shiftId: shift.shiftId,
                shiftName: shift.shiftName,
                vehicleShift: shift.vehicleShift,
                durationHours,
                monthlyHours,
                totalHours: Math.round(totalHours * 100) / 100,
                totalServiceDays,
                contractIds
            };
        });
    }

    getServiceContractLookup() {
        if (this._serviceContractLookup) {
            return this._serviceContractLookup;
        }

        const serviceContracts = {};
        const tripContracts = {};
        const routesById = keyBy(this.data.routes || [], 'route_id');

        (this.data.trips || []).forEach(trip => {
            if (!trip || !trip.service_id) return;
            const route = routesById[trip.route_id];
            const contractId = route?.contractId || route?.network_id || route?.agency_id || '';
            if (!contractId) return;

            if (!serviceContracts[trip.service_id]) {
                serviceContracts[trip.service_id] = new Set();
            }
            serviceContracts[trip.service_id].add(contractId);

            if (!tripContracts[trip.trip_id]) {
                tripContracts[trip.trip_id] = new Set();
            }
            tripContracts[trip.trip_id].add(contractId);
        });

        this._serviceContractLookup = { serviceContracts, tripContracts };
        return this._serviceContractLookup;
    }

    getShiftContractMap() {
        if (this._shiftContractMap) {
            return this._shiftContractMap;
        }

        const { serviceContracts, tripContracts } = this.getServiceContractLookup();
        const shiftEntriesById = {};

        (this.data.shifts || []).forEach(entry => {
            const shiftId = entry.shift_id;
            if (!shiftId) return;
            if (!shiftEntriesById[shiftId]) {
                shiftEntriesById[shiftId] = [];
            }
            shiftEntriesById[shiftId].push(entry);
        });

        const shiftContractMap = {};

        Object.entries(shiftEntriesById).forEach(([shiftId, entries]) => {
            const contractSet = new Set();

            entries.forEach(entry => {
                if (entry.trip_id && tripContracts[entry.trip_id]) {
                    tripContracts[entry.trip_id].forEach(contractId => contractSet.add(contractId));
                }
            });

            if (contractSet.size === 0) {
                entries.forEach(entry => {
                    if (entry.service_id && serviceContracts[entry.service_id]) {
                        serviceContracts[entry.service_id].forEach(contractId => contractSet.add(contractId));
                    }
                });
            }

            shiftContractMap[shiftId] = Array.from(contractSet).sort();
        });

        this._shiftContractMap = shiftContractMap;
        return this._shiftContractMap;
    }
} 